import { useCallback, useEffect, useRef, useState } from "react";
import { GripVertical } from "lucide-react";
import { SITE } from "../lib/site";

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const [beforeLoaded, setBeforeLoaded] = useState(false);
  const [afterLoaded, setAfterLoaded] = useState(false);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const ready = beforeLoaded && afterLoaded;

  // Preload both images to prevent flicker
  useEffect(() => {
    const b = new Image();
    b.onload = () => setBeforeLoaded(true);
    b.src = SITE.beforeAfter.before;
    const a = new Image();
    a.onload = () => setAfterLoaded(true);
    a.src = SITE.beforeAfter.after;
  }, []);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(x);
    };
    const onUp = () => (draggingRef.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [updateFromClientX]);

  const startDrag = (e) => {
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(x);
  };

  return (
    <section
      id="before-after"
      className="py-20 lg:py-28 bg-[#FAFAFA]"
      data-testid="before-after-section"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
        <p className="tc-eyebrow mb-3">See the Difference</p>
        <h2 className="font-serif text-[34px] sm:text-[44px] lg:text-[52px] text-[#173B86] leading-[1.05] tracking-[-0.01em]">
          The Transformation Speaks
        </h2>
        <p className="text-[14.5px] sm:text-[15px] text-[#5b6a85] mt-3 max-w-[600px] mx-auto">
          Drag the slider to reveal the difference. Same room, extraordinary
          results.
        </p>

        <div
          ref={containerRef}
          className="relative mt-10 lg:mt-12 select-none rounded-2xl overflow-hidden shadow-lg cursor-ew-resize touch-none mx-auto max-w-[1100px] aspect-[16/9] bg-[#e8ecf3]"
          onMouseDown={ready ? startDrag : undefined}
          onTouchStart={ready ? startDrag : undefined}
          data-testid="before-after-slider"
        >
          {/* AFTER as base layer (right side visible) */}
          <img
            src={SITE.beforeAfter.after}
            alt="After cleaning"
            className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300 ${
              ready ? "opacity-100" : "opacity-0"
            }`}
            draggable={false}
          />
          {/* BEFORE clipped on left */}
          <div
            className={`absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-300 ${
              ready ? "opacity-100" : "opacity-0"
            }`}
            style={{ width: `${pos}%` }}
          >
            <img
              src={SITE.beforeAfter.before}
              alt="Before cleaning"
              className="absolute inset-0 h-full object-cover object-left"
              style={{
                width: containerRef.current?.offsetWidth || "100%",
                maxWidth: "none",
              }}
              draggable={false}
            />
          </div>

          {/* Skeleton while loading */}
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center text-[13px] text-[#5b6a85]" data-testid="before-after-loading">
              Loading preview…
            </div>
          )}

          {/* Labels */}
          {ready && (
            <>
              <span className="absolute top-4 left-4 bg-[#183B87] text-white text-[11px] tracking-[0.18em] font-semibold px-3 py-1.5 rounded-md">
                BEFORE
              </span>
              <span className="absolute top-4 right-4 bg-white text-[#173B86] text-[11px] tracking-[0.18em] font-semibold px-3 py-1.5 rounded-md shadow">
                AFTER
              </span>

              <div
                className="absolute top-0 bottom-0 w-[2px] bg-white/90"
                style={{ left: `calc(${pos}% - 1px)` }}
              />
              <button
                type="button"
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center"
                style={{ left: `${pos}%` }}
                aria-label="Drag to compare"
                data-testid="before-after-handle"
                onMouseDown={startDrag}
                onTouchStart={startDrag}
              >
                <GripVertical className="w-4 h-4 text-[#173B86]" />
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
